import type { JsonSchema } from '@restore/reform';
import { baseApiUrl } from 'src/config';
import { getAccessToken } from 'src/routes/AuthContext';

export const submitReform = async (
  realm: string,
  formId: string,
  uuid: string,
  formData: JsonSchema,
  facilityId: string,
  formType?: string
): Promise<{ success: boolean; error?: string; errorCode?: string; errorData?: any }> => {
  const token = await getAccessToken();
  const apiUrl = formType?.startsWith('TRANSITIONAL_EVALUATION')
    ? `${baseApiUrl}/document/form/transitional-eval/${formId}?webhookUuid=${uuid}`
    : `${baseApiUrl}/document/form/${formId}?webhookUuid=${uuid}`;

  return fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-tenant-id': realm,
      Facility_id: facilityId,
      // THIN-7280: initial-submit path. document-service captures this header on
      // /document/form/{formId} and forwards it to provider-service so the SUBMIT
      // audit row carries the submitter's session IP — same convention as the
      // supervisor cosigner-edit path (submitReformSupervisor.ts) and the existing
      // signPhysicianDocument / undoDocSignatureFlow / applyTherapistBulkSign calls.
      'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
    },
    body: JSON.stringify(formData),
  })
    .then(async (response) => {
      if (response.status === 200) {
        return { success: true };
      }

      // Try to extract error message from response
      let errorMessage = 'Something went wrong';
      let errorCode: string | undefined;
      let errorData: any = null;
      try {
        const errorResponse = await response.json();
        errorData = errorResponse;
        if (errorResponse.message) {
          errorMessage = errorResponse.message;
        } else if (errorResponse.error) {
          errorMessage = errorResponse.error;
        } else if (typeof errorResponse === 'string') {
          errorMessage = errorResponse;
        }
        // Extract error code from message if it contains NO_PAYER_ON_CERTIFICATION_START_DATE
        // The error type is in the message field, not the code field
        if (
          errorResponse.message &&
          errorResponse.message.includes('NO_PAYER_ON_CERTIFICATION_START_DATE')
        ) {
          errorCode = 'NO_PAYER_ON_CERTIFICATION_START_DATE';
        } else if (errorResponse.code) {
          errorCode = errorResponse.code;
        }
      } catch (e) {
        // If parsing fails, use status text or default message
        errorMessage = response.statusText || `Request failed with status ${response.status}`;
      }

      return { success: false, error: errorMessage, errorCode, errorData };
    })
    .catch((error) => {
      return { success: false, error: `Error submitting form: ${error.message || error}` };
    });
};
